import { type PropsWithChildren, createContext, useContext, useMemo } from 'react';

/**
 * Context value for the WAC Refresh visual generation.
 *
 * This package deliberately knows nothing about *how* the value is resolved.
 * Feature gates, experiments and enrollment live in the consuming application;
 * the design system only needs the resolved boolean so components can switch
 * their visual treatment.
 */
type WacRefreshContextValue = {
	isWacRefresh: boolean;
};

const WacRefreshContext = createContext<WacRefreshContextValue>({
	isWacRefresh: false,
});

/**
 * Provides the resolved `isWacRefresh` flag to the component tree.
 *
 * Consumers are responsible for computing the value. In `wac-web` this is
 * wrapped by an adapter that resolves feature gates and experiments; in
 * prototypes, stories and tests it can be passed literally.
 *
 * Defaults to `false`, matching the context default, so stories and tests can
 * render without opting in.
 *
 * @example
 * <WacRefreshProvider isWacRefresh>
 *   <App />
 * </WacRefreshProvider>
 */
export const WacRefreshProvider = ({
	isWacRefresh = false,
	children,
}: PropsWithChildren<{ isWacRefresh?: boolean }>) => {
	const value = useMemo<WacRefreshContextValue>(() => ({ isWacRefresh }), [isWacRefresh]);

	return <WacRefreshContext.Provider value={value}>{children}</WacRefreshContext.Provider>;
};

/**
 * Returns `true` when the WAC Refresh visual generation is active.
 *
 * Defaults to `false` when used outside of a `<WacRefreshProvider>` — for
 * example in unit tests that render components in isolation.
 */
export const useWacRefresh = (): boolean => {
	const { isWacRefresh } = useContext(WacRefreshContext);
	return isWacRefresh;
};
